import { AuthService } from 'services/auth';
import { CONFIG } from 'config';
import {
  Payroll,
  IMonthlyWagesData,
  PayrollOnboardingStatus,
  PayrollOnboardingStatusResp,
  CompanyBankAccount,
  CompanyForm,
  SignCompanyFormBody,
  WebhookResp,
  Webhook,
  PayrollFlowType,
  GustoAuthorizationStatusResp,
  GustoAuthorizationStatus,
  Employee,
  Officer,
} from 'models/payroll';
import { ApiListResp } from 'models/api';
import { defaultTablePageSize } from 'constants/common';
import { payrollService, apiV2, AxiosResponse } from './axios';

const redirectURI = CONFIG.gustoRedirectUri;

export interface GustoAccountResp {
  id: number;
  email: string;
}
export const getCurrentGustoAccount = async (): Promise<GustoAccountResp[]> =>
  payrollService.get<GustoAccountResp[]>('/me').then((resp) => resp.data);

export interface GustoTokenHealthResp {
  isConnected: boolean;
  message?: string;
}
export const getGustoTokenHealth = async (
  businessID: string,
): Promise<GustoTokenHealthResp> =>
  apiV2
    .get<GustoTokenHealthResp>(`/payrolls/${businessID}/gusto-health-check`)
    .then((resp) => resp.data);

export interface GetGustoCompaniesParams {
  connected?: boolean;
}
export interface GustoCompanyResp {
  id: number;
  name: string;
  uuid: string;
}
export const getGustoCompanies = async (
  params: GetGustoCompaniesParams = {},
): Promise<GustoCompanyResp[]> =>
  payrollService
    .get<GustoCompanyResp[]>('/companies', { params })
    .then((resp) => resp.data);

export const processGustoAuth = async (
  code: string,
  isPrimaryAccessToken: boolean,
) => {
  const userId = AuthService.userId();
  return payrollService.post(
    '/auth',
    {},
    {
      params: {
        code,
        redirectURI,
        userId,
        isPrimary: isPrimaryAccessToken,
      },
    },
  );
};

const transformGustoAuthorizationStatus = (
  resp: GustoAuthorizationStatusResp,
): GustoAuthorizationStatus => ({
  authorized: resp.authorized,
  authorizedType: resp.authorized_type,
  companyUUID: resp.company_uuid,
  companyName: resp.company_name,
  error: resp.error,
  errorCode: resp.error_code,
});
export const getAuthorizationStatusForAccount = async (
  accountId: string,
): Promise<GustoAuthorizationStatus> =>
  payrollService
    .get<GustoAuthorizationStatusResp>(`/auth/${accountId}`)
    .then((resp) => transformGustoAuthorizationStatus(resp.data));

export const getAuthorizeURLForBusiness = async (businessID: string) =>
  apiV2
    .get<{ authUrl: string }>(`/payrolls/${businessID}/gusto-auth-url`)
    .then((resp) => resp.data?.authUrl ?? '');

export const authorizeGustoForAccount = async (
  businessID: string,
  code: string,
  state: string,
) =>
  apiV2.post<{ code: string; redirect_uri: string; state: string }, void>(
    `/payrolls/${businessID}/gusto-auth`,
    {
      code,
      redirect_uri: redirectURI,
      state,
    },
  );

export const deleteGustoAuthorization = async (businessID: string) =>
  apiV2.delete(`/payrolls/${businessID}/gusto-auth`);

export const connectGustoCompany = async (accountId: string, uuid: string) =>
  payrollService.post(`/accounts/${accountId}/connect-company`, {
    gustoCompanyUUID: uuid,
  });

export const createGustoCompany = async (businessID: string) =>
  apiV2.post(
    `/payrolls/${businessID}/gusto-company`,
    {},
    {
      timeout: 60000, // the request can take a while to complete
    },
  );

export const fallbackZero = (val: number | undefined): number => val ?? 0;
export const fallbackNull = (val: number | undefined | null): number | null =>
  val ?? null;

export interface GetPayrollsParams {
  page?: number;
  size?: number;
  from?: string;
  to?: string;
}
export const getPayrolls = (
  businessID: string,
  params?: GetPayrollsParams,
): Promise<ApiListResp<Payroll[]>> => {
  const { page = 1, size = defaultTablePageSize, from, to } = params ?? {};
  return apiV2
    .get<ApiListResp<Payroll[]>>(`/payrolls/${businessID}/payrolls`, {
      params: { page, size, from, to },
    })
    .then((data) => data.data);
};

export const getUpcomingPayrolls = (businessID: string): Promise<Payroll[]> =>
  apiV2
    .get<Payroll[]>(`/payrolls/${businessID}/upcoming-payrolls`)
    .then((resp) => resp.data);

export const calculatePayroll = (businessID: string, payrollUUID: string) =>
  apiV2.post(`/payrolls/${businessID}/payrolls/${payrollUUID}/calculate`);

export const downloadPayStub = async (
  businessID: string,
  payrollUUID: string,
  employeeUUID: string,
): Promise<Blob> => {
  const { data } = await apiV2.get<Blob>(
    `payrolls/${businessID}/pay-stubs/${payrollUUID}/${employeeUUID}/download`,
    {
      responseType: 'blob',
      timeout: 30000,
    },
  );
  return data;
};

export const getMonthlyWages = async (
  businessID: string,
): Promise<IMonthlyWagesData[]> =>
  apiV2
    .get<IMonthlyWagesData[]>(`/payrolls/${businessID}/monthly-wages`)
    .then((data) => data.data);

export const createLinkTokenForGusto = async (businessID: string) =>
  apiV2
    .post<{ linkToken: string }>(`payrolls/${businessID}/plaid-link-token`)
    .then((resp) => ({
      linkToken: resp.data.linkToken,
    }));

export const connectBank = async (
  companyID: string,
  publicToken: string,
  bankName: string,
  bankId: string,
) =>
  apiV2.post(`payrolls/${companyID}/bank-connections`, {
    publicToken,
    institutionName: bankName,
    institutionID: bankId,
  });

export const getBankAccounts = async (
  businessID: string,
): Promise<CompanyBankAccount[]> =>
  apiV2
    .get<CompanyBankAccount[]>(`payrolls/${businessID}/bank-accounts`)
    .then((resp) => resp.data);

const transformOnboardingStatus = (
  resp: PayrollOnboardingStatusResp,
): PayrollOnboardingStatus => ({
  ...resp,
  isStepCompleted: (id: string) =>
    resp.onboardingSteps.some((step) => step.id === id && step.completed),
});

export const getPayrollOnboardingStatus = async (
  companyUUID: string,
): Promise<PayrollOnboardingStatus> =>
  apiV2
    .get<PayrollOnboardingStatusResp>(
      `payrolls/${companyUUID}/onboarding-status`,
    )
    .then((resp) => transformOnboardingStatus(resp.data));

export const acceptTermsOfService = async (businessID: string): Promise<void> =>
  apiV2.post(`/payrolls/${businessID}/terms-of-service/accept`);

export const migrateEmbeddedPayroll = async (
  businessID: string,
): Promise<void> => apiV2.put(`/payrolls/${businessID}/migrate`);

export const getAcceptTermsOfServiceStatus = async (
  businessID: string,
): Promise<boolean> =>
  apiV2
    .get<{ latestTermsAccepted: boolean }>(
      `/payrolls/${businessID}/terms-of-service-status`,
    )
    .then((resp) => resp.data.latestTermsAccepted ?? false);

export const getOnboardingFlow = async (businessID: string): Promise<string> =>
  apiV2
    // eslint-disable-next-line camelcase
    .post<unknown, AxiosResponse<{ url: string }>>(
      `/payrolls/${businessID}/flow`,
      {
        flowType: 'onboarding',
      },
    )
    .then((resp) => resp.data.url || '');

export const checkPayrollReady = async (
  businessID: string,
): Promise<{ string: boolean }> =>
  apiV2
    .get(`/payrolls/${businessID}/onboarding/readiness`)
    .then((resp) => resp.data);

export const startCustomerOnboarding = async (businessID: string) =>
  apiV2.post(`/payrolls/${businessID}/customer-onboarding`);

export const updatePayrollOnboardingStatus = async (
  businessID: string,
  status: string,
) =>
  apiV2
    .patch(`/payrolls/${businessID}/onboarding`, { status })
    .then((resp) => resp.data);

export const getCompanyForms = async (
  businessID: string,
): Promise<CompanyForm[]> =>
  apiV2
    .get<CompanyForm[]>(`/payrolls/${businessID}/company-forms`)
    .then((resp) => resp.data);

export const downloadCompanyFormPdf = async (
  businessID: string,
  formId: string,
) =>
  apiV2
    .get<Blob>(`/payrolls/${businessID}/company-forms/${formId}/pdf`, {
      responseType: 'blob',
      timeout: 30000,
    })
    .then((resp) => resp.data);

export const signCompanyForm = async (
  businessID: string,
  formId: string,
  data: SignCompanyFormBody,
) => apiV2.put(`/payrolls/${businessID}/company-forms/${formId}/sign`, data);

export const completeOnboarding = async (businessID: string) =>
  apiV2.put(`/payrolls/${businessID}/onboarding/finish`);

const transformWebhook = (item: WebhookResp): Webhook => ({
  subscriptionTypes: item.subscription_types,
  status: item.status,
  uuid: item.uuid,
});

export const getWebhook = async () =>
  apiV2
    .get<WebhookResp[]>('/subscription')
    .then((resp) => resp.data.map(transformWebhook));

export const createWebhook = async () => apiV2.post<void>('/subscription');

// eslint-disable-next-line camelcase
type CreatePayrollFlowResp = { url: string };
export const createPayrollFlow = async (
  businessID: string,
  flowType: PayrollFlowType,
) =>
  apiV2
    .post<unknown, AxiosResponse<CreatePayrollFlowResp>>(
      `/payrolls/${businessID}/flow`,
      {
        flowType,
      },
    )
    .then((resp) => resp.data.url);

export enum PayrollRequestType {
  PAYROLL_PLAN = 'payroll_plan',
  OFFCYCLE = 'offcycle',
  SKIP = 'skip',
}

export type PayrollRequest = {
  payrollUUID?: string;
  note: string;
  requestDate?: string;
  requestAmount?: number;
  type: PayrollRequestType;
  payrollCheckDate?: string;
  payrollPeriod?: string;
};

export const createPayrollRequest = async (
  businessID: string,
  request: PayrollRequest,
) => apiV2.post(`/payrolls/${businessID}/payroll-request`, request);

export const getEmployees = async (businessId: string): Promise<Employee[]> =>
  apiV2.get<Employee[]>(`/payrolls/${businessId}/employees`).then((resp) =>
    resp.data.map((item) => ({
      uuid: item.uuid,
      lastName: item.lastName,
      firstName: item.firstName,
    })),
  );

export const getEmployeeDocument = async (
  businessId: string,
  employeeUUID: string,
  documentUUID: string,
) =>
  apiV2
    .get<Blob>(
      `/payrolls/${businessId}/employees/${employeeUUID}/documents/${documentUUID}/download`,
      {
        responseType: 'blob',
        timeout: 30000,
      },
    )
    .then((resp) => resp.data);

interface EmployeeDocumentResp {
  uuid: string;
  name: string;
  title: string;
  year: number;
}

export const getAvailableW2Documents = async (
  businessId: string,
  employeeUUID: string,
) =>
  apiV2
    .get<EmployeeDocumentResp[]>(
      `/payrolls/${businessId}/employees/${employeeUUID}/documents-w2`,
    )
    .then((resp) => resp.data);

export const getOfficers = async (businessID: string): Promise<Officer[]> =>
  apiV2
    .get<Officer[]>(`/payrolls/${businessID}/officers`)
    .then((resp) => resp.data);

export const saveOfficers = async (businessID: string, officers: string[]) =>
  apiV2.put(`/payrolls/${businessID}/officers`, {
    officers,
  });

export const setDisabledPayrollFeature = async (
  businessID: string,
  disabledPayroll: boolean,
) =>
  apiV2.put(`/payrolls/${businessID}/settings`, {
    disabledPayroll,
  });
